import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router';
import classNames from 'classnames';
import useTranslation from '~/app/hooks/useTranslation';
import usePageTitle from '~/app/hooks/usePageTitle';
import { useAppHeader } from '~/app/modules/appHeader';
import { route } from '~/app/helpers/route';
import { notify } from '~/app/helpers/notify';
import Button from '~/app/common/Button';
import Loader from '~/app/common/Loader';
import BottomPopup from '~/app/common/BottomPopup';
import Icon from '~/app/common/Icon';
import { IssueOrServiceType } from '~/app/data/statuses/chat';
import { useStoreActions, useStoreState } from '~/store';
import TaskForm, { taskKey } from '~/workorder/Add/TaskForm';
import { WORKORDER_ROUTES } from '~/workorder/Router';
import { WorkOrderExcavatorTask } from '~/workorder/types';
import classes from './style.module.scss';

export type AddTaskComponent = React.FC;

const AddTask: AddTaskComponent = () => {
    const { id } = useParams<{ id: string }>();
    const history = useHistory();
    const { setData } = useAppHeader();
    const { t } = useTranslation('workorder');
    const { t: tc } = useTranslation('workorder.create');

    const fetching = useStoreState(state => state.workOrder.fetching);
    const workOrderData = useStoreState(state => state.workOrder.data);
    const getWorkOrderDetail = useStoreActions(actions => actions.workOrder.getWorkOrderDetail);
    const saveWorkOrderTasks = useStoreActions(actions => actions.workOrder.saveWorkOrderTasks);

    const [selectedTasks, setSelectedTasks] = useState<WorkOrderExcavatorTask[]>([]);
    const [reviewOpen, setReviewOpen] = useState(false);
    const [saving, setSaving] = useState(false);

    const pageTitle = t('detail.addTaskTitle');
    usePageTitle(pageTitle);

    const disabledTaskKeys = useMemo(() => {
        const keys = new Set<string>();
        (workOrderData.tasks ?? []).forEach(task => {
            keys.add(taskKey({ id: task.id, type: task.type }));
        });
        return keys;
    }, [workOrderData.tasks]);

    useEffect(() => {
        if (!workOrderData.id || workOrderData.id !== Number(id)) {
            getWorkOrderDetail({ id: Number(id) });
        }
    }, [getWorkOrderDetail, workOrderData.id, id]);

    useEffect(() => {
        setData({
            title: pageTitle,
            subTitle: '',
            backLink: history.length > 1 ? history.goBack : route(WORKORDER_ROUTES.detail, { id }),
            showUser: false,
            machineId: '',
            projectId: '',
            showMachineMessages: false,
            isIssueDetailPage: false,
            notification: false,
            search: false,
        });
    }, [setData, pageTitle, history.length, history.goBack, id]);

    const handleOpenReview = useCallback(() => {
        if (!selectedTasks.length || saving) {
            return;
        }
        setReviewOpen(true);
    }, [selectedTasks.length, saving]);

    const handleRemoveFromReview = useCallback((task: WorkOrderExcavatorTask) => {
        setSelectedTasks(prev => prev.filter(item => taskKey(item) !== taskKey(task)));
    }, []);

    const handleConfirmSave = useCallback(() => {
        if (!selectedTasks.length || saving) {
            return;
        }

        setSaving(true);
        saveWorkOrderTasks({ workOrderId: Number(id), tasks: selectedTasks })
            .then(response => {
                if (response.success) {
                    notify(t('detail.taskAdded'), 'success');
                    return getWorkOrderDetail({ id: Number(id), showLoading: false }).then(() => {
                        history.push(route(WORKORDER_ROUTES.detail, { id }));
                    });
                }
                notify(t('detail.addTaskError'), 'error');
            })
            .catch(() => {
                notify(t('detail.addTaskError'), 'error');
            })
            .finally(() => {
                setSaving(false);
            });
    }, [selectedTasks, saving, saveWorkOrderTasks, id, t, getWorkOrderDetail, history]);

    const renderSubtitle = useCallback((task: WorkOrderExcavatorTask) => {
        if (task.type === IssueOrServiceType.ISSUE) {
            return `${task.description} - ${task.date}`;
        }
        return tc(`taskType.${task.type}`);
    }, [tc]);

    if (fetching && !workOrderData.excavator?.id) {
        return <Loader size="32" />;
    }

    const machineId = String(workOrderData.excavator?.id || '');
    const selectedCount = selectedTasks.length;

    return (
        <div className={classes.page}>
            <TaskForm
                machineId={machineId}
                selectedTasks={selectedTasks}
                onSelectedTasksChange={setSelectedTasks}
                disabledTaskKeys={disabledTaskKeys}
            />
            <div className={classes.footer}>
                <span className={classes.selectedCount}>
                    {t('detail.selectedCount', { count: selectedCount })}
                </span>
                <Button
                    type="button"
                    color="primary"
                    className={classes.confirm}
                    disabled={!selectedCount || saving}
                    onClick={handleOpenReview}
                >
                    {t('detail.addTaskConfirm')}
                </Button>
            </div>

            <BottomPopup show={reviewOpen} onClose={() => setReviewOpen(false)} fromBottom>
                <h3 className={classes.reviewHeader}>
                    {t('detail.reviewTasksTitle', { count: selectedCount })}
                </h3>
                {!selectedCount ? (
                    <p className={classes.reviewEmpty}>{tc('tasks.empty')}</p>
                ) : (
                    <div className={classes.reviewList}>
                        {selectedTasks.map(task => {
                            const isIssue = task.type === IssueOrServiceType.ISSUE;
                            return (
                                <div key={taskKey(task)} className={classes.reviewRow}>
                                    <div
                                        className={classNames(
                                            classes.typeIconWrap,
                                            isIssue ? classes.issue : classes.service,
                                        )}
                                    >
                                        <Icon name={isIssue ? 'warning' : 'build'} />
                                    </div>
                                    <div className={classes.reviewContent}>
                                        <p className={classes.reviewTitle}>{task.title}</p>
                                        <p className={classes.reviewSubtitle}>{renderSubtitle(task)}</p>
                                    </div>
                                    <Icon
                                        name="close"
                                        className={classes.removeIcon}
                                        onClick={() => handleRemoveFromReview(task)}
                                    />
                                </div>
                            );
                        })}
                    </div>
                )}
                <div className={classes.reviewFooter}>
                    <Button
                        type="button"
                        color="primary"
                        block
                        className={classes.reviewConfirm}
                        disabled={!selectedCount || saving}
                        onClick={handleConfirmSave}
                    >
                        {t('detail.reviewConfirm', { count: selectedCount })}
                    </Button>
                </div>
            </BottomPopup>
        </div>
    );
};

export default AddTask;
